import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import Form from "../Form/Form.jsx";
import FormPasswordField from "../Form/controls/FormPasswordField.jsx";
import SubmitButton from "../Form/controls/SubmitButton.jsx";
import {
  requiredPasswordSchema,
  passwordConfirmationSchema,
  passwordHelperText,
} from "../../utils/passwordValidation.js";

// Validation schema for password reset form
const validationSchema = yup.object({
  password: requiredPasswordSchema,
  password_confirmation: passwordConfirmationSchema("password"),
});

// Visually hidden but still present in the DOM/accessibility tree, so
// password managers can pick up the associated email for this password.
const visuallyHiddenStyle = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Password reset form component for setting a new password with a token
 *
 * @param {Object} props - Component props
 * @param {string} props.token - Reset token from email link
 * @param {string} props.email - Email address the reset applies to
 * @param {function} props.onSubmit - Callback when form is submitted with valid data
 * @param {boolean} props.isLoading - Whether the form is in loading state
 * @returns {JSX.Element}
 */
const PasswordResetForm = ({
  token = "",
  email = "",
  onSubmit,
  isLoading = false,
}) => {
  const handleFormSubmit = async (data, formMethods) => {
    console.log("Form submit started with data:", data);
    console.log("Form methods available:", Object.keys(formMethods));

    if (onSubmit) {
      const success = await onSubmit(
        {
          token: token.trim(),
          password: data.password,
          passwordConfirmation: data.password_confirmation,
        },
        formMethods
      ); // Pass formMethods to parent for server error handling

      console.log("Password reset success?", success);
      console.log("Form errors after onSubmit:", formMethods.formState.errors);

      // If submission was successful, optionally reset the form
      if (success) {
        formMethods.reset();
      }

      return success; // Return the actual success value, not always true
    }
    return false; // Return false if no onSubmit callback
  };

  return (
    <Form
      onSubmit={handleFormSubmit}
      resolver={yupResolver(validationSchema)}
      defaultValues={{
        password: "",
        password_confirmation: "",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          component="input"
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          readOnly
          tabIndex={-1}
          sx={visuallyHiddenStyle}
        />
        <FormPasswordField
          name="password"
          label="Passwort"
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          helperText={passwordHelperText.requirements}
        />
        <FormPasswordField
          name="password_confirmation"
          label="Passwort (Wiederholung)"
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          helperText="Bestätigen Sie Ihr neues Passwort"
        />
        <SubmitButton disabled={isLoading}>
          {isLoading ? "Wird gesetzt..." : "Passwort setzen"}
        </SubmitButton>
      </Box>
    </Form>
  );
};

PasswordResetForm.propTypes = {
  token: PropTypes.string,
  email: PropTypes.string,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default PasswordResetForm;
